import { field, formatUnits, parseEther, readContract, toBool, writeContractAndWait } from "../viem";

import MafiaSlotMachineAbi from "../../abis/MafiaSlotMachine.json";
import config from "../../config/config";
import { SlotMachine } from "../../types/Contract/SlotMachine/SlotMachine";
import { SlotMachineBetInfo } from "../../types/Contract/SlotMachine/SlotMachineBetInfo";

const address = config.slotAddress;
const abi = MafiaSlotMachineAbi;

export const depositLiquidity = async (
  account: string,
  slotMachineId: number,
  amount: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "depositLiquidity",
    args: [slotMachineId, parseEther(amount.toString())],
    account,
  });

  return data;
};

export const removeLiquidity = async (
  account: string,
  slotMachineId: number,
  amount: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "removeLiquidity",
    args: [slotMachineId, parseEther(amount.toString())],
    account,
  });

  return data;
};

export const updateSlotMachineSettings = async (
  account: string,
  slotMachineId: number,
  // isOpened: boolean,
  minBet: number,
  maxBet: number,
  message: string,
  signature: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "updateSlotMachineSettings",
    args: [
      slotMachineId,
      parseEther(minBet.toString()),
      parseEther(maxBet.toString()),
      JSON.stringify(message),
      signature,
    ],
    account,
  });

  return data;
};

export const getSlotMachineLiquidity = async (
  slotMachineId: number,
  message: string,
  signature: string
) => {
  const liquidity: number = await readContract({
    address,
    abi,
    functionName: "getSlotMachineLiquidity",
    args: [slotMachineId, JSON.stringify(message), signature],
  });

  return parseInt(formatUnits(liquidity, 18));
};

export const getSlotMachines = async () => {
  const slotMachines: any[] = await readContract({
    address,
    abi,
    functionName: "getSlotMachines",
  });

  const parsedSlotMachineList: SlotMachine[] = slotMachines.map(
    (slotMachine: any) => {
      return {
        id: Number(field(slotMachine, "id", 0)),
        isOpened: field(slotMachine, "isOpened", 1),
        minBet: parseInt(formatUnits(field(slotMachine, "minBet", 4), 18)),
        maxBet: parseInt(formatUnits(field(slotMachine, "maxBet", 3), 18)),
        minBetBottom: parseInt(formatUnits(field(slotMachine, "minBetBottom", 6), 18)),
        minBetTop: parseInt(formatUnits(field(slotMachine, "minBetTop", 5), 18)),
        maxBetBottom: parseInt(formatUnits(field(slotMachine, "maxBetBottom", 8), 18)),
        maxBetTop: parseInt(formatUnits(field(slotMachine, "maxBetTop", 7), 18)),
        ownedAt: Number(field(slotMachine, "ownedAt", 2)),
        feesPaid: parseInt(formatUnits(field(slotMachine, "feesPaid", 9), 18)),
        inventoryItemId: Number(field(slotMachine, "inventoryItemId", 10)),
        profit: parseInt(formatUnits(field(slotMachine, "profit", 11), 18)),
      } as SlotMachine;
    }
  );

  return parsedSlotMachineList;
};

export const getUserBetInfo = async (
  account: string,
  slotMachineId: number
) => {
  const betInfo: SlotMachineBetInfo = await readContract({
    address,
    abi,
    functionName: "userBetInfo",
    args: [account, slotMachineId],
    account,
    useWallet: true,
  });

  return {
    isPending: toBool(field(betInfo, "isPending", 0)),
    requestBlock: Number(field(betInfo, "requestBlock", 1)),
    totalAmount: field(betInfo, "totalAmount", 2),
  } as SlotMachineBetInfo;
};

export const initializeBet = async (
  account: string,
  slotMachineId: number,
  spinCount: number,
  amountPerSpin: number,
  signature: string,
  message: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "initializeBet",
    args: [
      slotMachineId,
      spinCount,
      parseEther(amountPerSpin.toString()),
      JSON.stringify(message),
      signature,
    ],
    account,
  });

  return data;
};

export const finishBet = async (account: string, slotMachineId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "finishBet",
    args: [slotMachineId],
    account,
  });

  return data;
};

export const getNonceStatus = async (
  account: string,
  slotMachineId: number
) => {
  const data: any = await readContract({
    address,
    abi,
    functionName: "getNonceStatus",
    args: [account, slotMachineId],
    account,
    useWallet: true,
  });

  return [toBool(field(data, "isPending", 0)), toBool(field(data, "isFulfilled", 1))];
};

export const getBets = async (account: string, betIds: number[]) => {
  const bets: any[] = await readContract({
    address,
    abi,
    functionName: "getBets",
    args: [betIds],
    account,
  });

  return bets.map((bet, index) => {
    return {
      id: bet.id,
      player: bet.player,
      slotMachineId: bet.slotMachineId,
      spinCount: bet.spinCount,
      amountPerSpin: bet.amountPerSpin,
      reward: bet.reward,
      nonces: bet.nonces.map((nonce: any) => Number(nonce)),
    };
  })[0];
};
